import { useI18n } from 'vue-i18n';
import { date } from 'quasar';
import { computed, onUnmounted, ref } from 'vue';
import { Subscription } from 'rxjs';
import useObject from '@/shared/lib/use-object';
import { formatDateIso } from '@/shared/lib/date';
import { useAuthUserStore } from '@/app/stores/user';
import { CurrencyForAccountFragment } from '@/app/graphql/account/sdk.account';
import { AccountStatementForm } from '@/features/documents/account-statement/order/lib';

import {
  downloadFileStatement,
  downloadPosStatement,
  findAllStatement,
  findPosStatements,
  isStatementOrderInputType,
  listenPosStatement,
  ListenStatement,
  listenStatement,
  orderPosStatement,
  orderStatement,
  PaginationPosTerminalStatement,
  PaginationStatement,
  Statement,
  userListenStatementLine,
} from '@/shared/services/statement';
import { FileFormat, PosTerminalStatementOrder, SubscriptionType } from '@/app/graphql/statement/sdk.statement';
import { downloadFile, downloadNativeFile, DownloadFileType } from '@/shared/web/helpers';
import { useLoader } from '@/shared/lib/use-loader';
import { ALL_POS_AND_KIOSKS_QR_SERVICE_ID, PosStatementForm } from '@/features/documents/pos-statement/order/lib';
import { LISTENER_ACTION_NATIVE, nativeApp, STATUS_BOTTOM_SHEET } from '@/shared/services/native-app';
import { useAnalytics } from '@/shared/lib/use-analitics';

const INITIAL_PAGINATION = { page: 1, size: 50 };
const INITIAL_STATEMENT = {
  totalPages: 0,
  currentPage: 0,
  statements: [],
};
const INITIAL_POS_STATEMENT = {
  totalPages: 0,
  currentPage: 0,
  elementsInCurrentPage: 0,
  totalElements: 0,
  posTerminalStatements: [],
};

interface ParamsDownloadFile {
  type: FileFormat;
  statement: Statement;
}
export function useStatement() {
  const { t } = useI18n();
  const { setIsShowLoading } = useLoader();
  const authUserStore = useAuthUserStore();
  const statement = ref<PaginationStatement>({ ...INITIAL_STATEMENT });
  const posStatement = ref<PaginationPosTerminalStatement>({ ...INITIAL_POS_STATEMENT });
  const loading = ref(false);
  const error = ref(false);
  const currentContract = computed(() => authUserStore.selectedUserContract);
  const unsubscribe = ref<Function>();
  const orderRef = ref();
  const pagination = ref({ ...INITIAL_PAGINATION });
  const formTimeInformation = ref<PosStatementForm | AccountStatementForm | null>(null);

  const userStatementLineSubscription = ref<Subscription | null>(null);
  const unsubscribeSubscription = () => {
    if (unsubscribe.value) unsubscribe.value();
    if (userStatementLineSubscription.value) userStatementLineSubscription.value.unsubscribe();
  };
  const listen = async () => {
    if (unsubscribe.value) unsubscribe.value();
    if (!currentContract.value?.orgReferenceId) return;
    unsubscribe.value = await listenStatement(currentContract.value.orgReferenceId);
    userStatementLineSubscription.value = userListenStatementLine.subscribe((message: Statement) => {
      const index = statement.value.statements.findIndex((item) => item.id === message.id);
      if (index === -1) return;
      statement.value.statements[index].statusLabel = message.statusLabel;
      statement.value.statements[index].status = message.status;
    });
  };

  const listenPos = async () => {
    if (unsubscribe.value) unsubscribe.value();
    if (!currentContract.value?.orgReferenceId) return;
    unsubscribe.value = await listenPosStatement(currentContract.value.orgReferenceId);
    userStatementLineSubscription.value = userListenStatementLine.subscribe((message: ListenStatement) => {
      const { id, statusLabel, status } = message.data.listen.statement;
      if (!id) return;
      const index = posStatement.value.posTerminalStatements.findIndex((item: any) => item.id === id);
      if (index === -1) return;

      if (message.data.listen.subscriptionType === SubscriptionType.Delete) {
        return posStatement.value.posTerminalStatements.splice(index, 1);
      }

      posStatement.value.posTerminalStatements[index].statusLabel = statusLabel;
      posStatement.value.posTerminalStatements[index].status = status;
    });
  };

  const getAllStatement = async () => {
    try {
      error.value = false;
      if (!currentContract.value?.orgReferenceId) return;
      statement.value = { ...INITIAL_STATEMENT };
      loading.value = true;
      statement.value = await findAllStatement(currentContract.value.orgReferenceId, pagination.value);
      await listen();
    } catch (e) {
      statement.value.statements = [];
      error.value = true;
    } finally {
      loading.value = false;
    }
  };

  const getPosStatements = async () => {
    try {
      error.value = false;
      if (!currentContract.value?.orgReferenceId) return;
      posStatement.value = { ...INITIAL_POS_STATEMENT };
      pagination.value = { ...INITIAL_PAGINATION };
      loading.value = true;
      posStatement.value = await findPosStatements(currentContract.value.orgReferenceId, pagination.value);
      await listenPos();
    } catch (e) {
      statement.value.statements = [];
      error.value = true;
    } finally {
      loading.value = false;
    }
  };

  const getNextStatements = async (done: Function) => {
    if (!currentContract.value?.orgReferenceId) return;
    if (pagination.value.page === statement.value.totalPages) return;
    pagination.value.page++;
    loading.value = true;
    try {
      const newStatement = await findAllStatement(currentContract.value.orgReferenceId, pagination.value);
      if (newStatement.statements.length) {
        statement.value.currentPage = newStatement.currentPage;
        statement.value.totalPages = newStatement.totalPages;
        statement.value.statements.push(...newStatement.statements);
        return done();
      }

      return done(true);
    } catch (e) {
      console.error(e);
    } finally {
      loading.value = false;
    }
  };

  const getNextPosStatements = async (done: Function) => {
    if (!currentContract.value?.orgReferenceId) return;
    if (pagination.value.page === statement.value.totalPages) return;
    pagination.value.page++;
    try {
      const newStatement = await findPosStatements(currentContract.value.orgReferenceId, pagination.value);
      if (newStatement.posTerminalStatements.length) {
        posStatement.value.currentPage = newStatement.currentPage;
        posStatement.value.totalPages = newStatement.totalPages;
        posStatement.value.posTerminalStatements.push(...newStatement.posTerminalStatements);
        return done();
      }

      return done(true);
    } catch (e) {
      console.error(e);
    }
  };

  const getDownloadFile = async (payload: ParamsDownloadFile) => {
    setIsShowLoading(true, t('DOCUMENTS.STATEMENT_ACCOUNT.DOWNLOADING'));
    try {
      const response = await downloadFileStatement(payload.type, payload.statement);
      const { data: content, contentType, fileName: filename } = response;
      downloadFile({ filename, contentType, content });
    } catch (e: any) {
      console.error(e.message);
    } finally {
      setIsShowLoading(false);
    }
  };

  const getDownloadPosFile = async (payload: ParamsDownloadFile) => {
    setIsShowLoading(true, t('DOCUMENTS.STATEMENT_ACCOUNT.DOWNLOADING'));
    try {
      const response = await downloadPosStatement(payload.statement, payload.type);
      const { data: content } = response;
      downloadNativeFile(content, payload.type === FileFormat.Xml ? DownloadFileType.XML : DownloadFileType.XLSX);
    } catch (e: any) {
      console.error(e.message);
    } finally {
      setIsShowLoading(false);
    }
  };

  const createAccountStatement = async (form: AccountStatementForm) => {
    useAnalytics(`click_button_create_account_statement`);
    try {
      if (!currentContract.value?.orgReferenceId) return;
      formTimeInformation.value = null;
      formTimeInformation.value = form;
      setIsShowLoading(true, t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER_FORMED'));
      const currency = form.currencies.map((currency: CurrencyForAccountFragment) => currency.isoNum);
      const payload = {
        account: form.account?.account ?? null,
        statementType: form.statementType,
        partyId: currentContract.value.orgReferenceId,
        currencies: currency.length ? currency : [],
        startDate: formatDateIso(form.startDate!),
        endDate: form.endDate ? formatDateIso(form.endDate) : date.formatDate(Date.now(), 'YYYY-MM-DD'),
      };
      const { removeEmpty } = useObject();
      const params = removeEmpty(payload);
      if (!params || !isStatementOrderInputType(params)) return;
      const response = await orderStatement(params);
      formTimeInformation.value = null;
      if (!response) return;

      orderRef.value?.clear();
      statement.value.statements.unshift(response);
      nativeApp.showBottomSheet({
        title: t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER_EXTRACT.IN_PROCESSING'),
        subtitle: '',
        mainAction: '',
        labelMainAction: t('SHARED.CLOSE'),
        labelDismissAction: '',
        dismissAction: '',
        status: STATUS_BOTTOM_SHEET.SUCCESS,
        persistent: false,
      });
    } catch (e: any) {
      nativeApp.showBottomSheet({
        title: t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER_EXTRACT.FAIL'),
        subtitle: '',
        mainAction: 'window.nativeHandler.bottomSheetAction("createAccountStatement")',
        labelMainAction: t('DIALOG.TRY_AGAIN'),
        labelDismissAction: t('DIALOG.RETURN'),
        dismissAction: '',
        status: STATUS_BOTTOM_SHEET.ERROR,
        persistent: false,
      });
    } finally {
      setIsShowLoading(false);
    }
  };

  const createPosStatement = async (form: PosStatementForm) => {
    try {
      if (!currentContract.value?.orgReferenceId) return;
      useAnalytics(`click_button_create_pos_statement`);
      setIsShowLoading(true, t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER_FORMED'));
      formTimeInformation.value = null;
      formTimeInformation.value = form;
      const payload: PosTerminalStatementOrder = {
        partyID: currentContract.value.orgReferenceId,
        posTerminalsByAddresses: [
          {
            address: t(form.salePoint?.name ?? ''),
            posTerminals: form.posNumbers?.map((item) => {
              return {
                id: 0,
                code: item,
              };
            }),
          },
        ],
        startDate: formatDateIso(form.startDate!),
        endDate: form.endDate ? formatDateIso(form.endDate) : date.formatDate(Date.now(), 'YYYY-MM-DD'),
        qrServiceId: form.isAllPosAndKiosks ? ALL_POS_AND_KIOSKS_QR_SERVICE_ID : null,
        isFilterByTransactionDate: form.isFilterByTransactionDate,
        salePointCode: form.salePoint?.code || null,
      };

      const response = await orderPosStatement(payload);
      formTimeInformation.value = null;
      if (!response) return;

      orderRef.value?.clear();
      posStatement.value.posTerminalStatements.unshift(response);
      nativeApp.showBottomSheet({
        title: t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER_EXTRACT.IN_PROCESSING'),
        subtitle: '',
        mainAction: '',
        labelMainAction: t('SHARED.ACTION.CLOSE'),
        labelDismissAction: '',
        dismissAction: '',
        status: STATUS_BOTTOM_SHEET.SUCCESS,
        persistent: false,
      });
    } catch (e: any) {
      nativeApp.showBottomSheet({
        title: t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER_EXTRACT.FAIL'),
        subtitle: '',
        mainAction: 'window.nativeHandler.bottomSheetAction("createPosStatement")',
        labelMainAction: t('DIALOG.TRY_AGAIN'),
        labelDismissAction: t('DIALOG.RETURN'),
        dismissAction: '',
        status: STATUS_BOTTOM_SHEET.ERROR,
        persistent: false,
      });
    } finally {
      setIsShowLoading(false);
    }
  };

  const activateAction = (action: Event) => {
    if ((action as CustomEvent)?.detail === 'createPosStatement' && formTimeInformation.value) {
      return createPosStatement(formTimeInformation.value as PosStatementForm);
    }
    if ((action as CustomEvent)?.detail === 'createAccountStatement' && formTimeInformation.value) {
      return createAccountStatement(formTimeInformation.value as AccountStatementForm);
    }
  };

  window.addEventListener(LISTENER_ACTION_NATIVE.BOTTOM_SHEET_ACTION, (e: Event) => activateAction(e), false);

  onUnmounted(() => {
    window.removeEventListener(LISTENER_ACTION_NATIVE.BOTTOM_SHEET_ACTION, (e: Event) => activateAction(e), false);
    unsubscribeSubscription();
  });

  return {
    t,
    error,
    loading,
    orderRef,
    statement,
    posStatement,
    currentContract,
    listen,
    listenPos,
    getDownloadFile,
    getDownloadPosFile,
    getAllStatement,
    getPosStatements,
    getNextStatements,
    getNextPosStatements,
    createAccountStatement,
    createPosStatement,
    unsubscribeSubscription,
  };
}
